import type { BatchRequest, BatchResult, GpuCapability, MiningBackend, MiningMode, MiningPowerMode } from "./types";
import type { MinerWorkerCommand, MinerWorkerEvent } from "./workers/worker-protocol";
import { CpuWorkerPool } from "./cpu-worker-pool";

type Pending = { resolve(value: BatchResult): void; reject(error: Error): void; abort?: () => void;
  progress?: ((completed: number) => void) | undefined };

class ModuleWorkerMiningBackend implements MiningBackend {
  private worker: Worker | null = null;
  private ready: Promise<void> | null = null;
  private readyResolve: (() => void) | null = null;
  private readyReject: ((error: Error) => void) | null = null;
  private requestSequence = 0;
  private readonly pending = new Map<number, Pending>();
  private initializationTimer: ReturnType<typeof setTimeout> | undefined;
  preferredBatchSize = 4096;

  constructor(readonly mode: MiningMode, private readonly createWorker: () => Worker,
    private readonly onCapability?: (capability: GpuCapability) => void) {}

  initialize(powerMode: MiningPowerMode): Promise<void> {
    if (this.ready) return this.ready;
    this.worker = this.createWorker();
    this.ready = new Promise<void>((resolve, reject) => {
      this.readyResolve = resolve;
      this.readyReject = reject;
    });
    this.initializationTimer = setTimeout(() => {
      this.readyReject?.(new Error(`${this.mode}_WORKER_INITIALIZATION_TIMEOUT`));
      this.dispose();
    }, this.mode === "GPU" ? 30_000 : 10_000);
    this.worker.onmessage = (event: MessageEvent<MinerWorkerEvent>) => this.onMessage(event.data);
    this.worker.onerror = () => {
      const error = new Error(`${this.mode}_WORKER_ERROR`);
      this.readyReject?.(error);
      this.readyResolve = null;
      this.readyReject = null;
      this.failAll(error);
      this.dispose();
    };
    this.post({ type: "INITIALIZE", powerMode });
    return this.ready;
  }

  async runBatch(request: BatchRequest, signal: AbortSignal, onProgress?: (completed: number) => void): Promise<BatchResult> {
    if (!this.ready || !this.worker) throw new Error(`${this.mode}_BACKEND_NOT_INITIALIZED`);
    await this.ready;
    if (signal.aborted) throw abortError();
    const requestId = ++this.requestSequence;
    return new Promise<BatchResult>((resolve, reject) => {
      let cancellationTimer: ReturnType<typeof setTimeout> | undefined;
      const onAbort = () => {
        this.post({ type: "CANCEL", requestId });
        // Harvest completed work (including candidates) before releasing the lease.
        cancellationTimer = setTimeout(() => {
          reject(new Error(`${this.mode}_WORKER_CANCEL_TIMEOUT`));
          this.dispose();
        }, 2_000);
      };
      signal.addEventListener("abort", onAbort, { once: true });
      this.pending.set(requestId, { resolve, reject, progress: onProgress, abort: () => {
        clearTimeout(cancellationTimer);
        signal.removeEventListener("abort", onAbort);
      } });
      this.post({ type: "MINE_BATCH", requestId, request });
    });
  }

  dispose(): void {
    clearTimeout(this.initializationTimer);
    const error = new Error(`${this.mode}_BACKEND_DISPOSED`);
    if (this.worker) this.post({ type: "DISPOSE" });
    this.worker?.terminate();
    this.worker = null;
    this.readyReject?.(error);
    this.readyResolve = null;
    this.readyReject = null;
    this.ready = null;
    this.preferredBatchSize = 4096;
    this.failAll(error);
  }

  private onMessage(event: MinerWorkerEvent): void {
    if (event.type === "CAPABILITY") { this.onCapability?.(event.capability); return; }
    if (event.type === "READY") {
      clearTimeout(this.initializationTimer);
      if (Number.isSafeInteger(event.preferredBatchSize) && event.preferredBatchSize! > 0) {
        this.preferredBatchSize = event.preferredBatchSize!;
      }
      if (event.capability) this.onCapability?.(event.capability);
      this.readyResolve?.();
      this.readyResolve = null;
      this.readyReject = null;
      return;
    }
    if (event.type === "ERROR" && event.requestId === undefined) {
      const error = new Error(event.code);
      this.readyReject?.(error);
      this.readyReject = null;
      this.failAll(error);
      this.dispose();
      return;
    }
    const requestId = event.requestId;
    if (requestId === undefined) return;
    const pending = this.pending.get(requestId);
    if (!pending) return;
    if (event.type === "PROGRESS") {
      try { pending.progress?.(event.completed); }
      catch (error) {
        this.pending.delete(requestId);
        pending.abort?.();
        pending.reject(error instanceof Error ? error : new Error(String(error)));
        this.dispose();
      }
      return;
    }
    this.pending.delete(requestId);
    pending.abort?.();
    if (event.type === "RESULT") pending.resolve(event.result);
    else if (event.type === "CANCELLED") pending.reject(abortError());
    else pending.reject(new Error(event.code));
  }

  private failAll(error: Error): void {
    for (const value of this.pending.values()) { value.abort?.(); value.reject(error); }
    this.pending.clear();
  }

  private post(command: MinerWorkerCommand): void { this.worker?.postMessage(command); }
}

export function createCpuWorkerBackend(): MiningBackend {
  // Keep new Worker(new URL(...)) in this exact static form so Vite bundles
  // TypeScript and its imports as an executable module-worker chunk.
  return new CpuWorkerPool(() => new ModuleWorkerMiningBackend("CPU", () => new Worker(
    new URL("./workers/cpu-miner.worker.ts", import.meta.url),
    { type: "module", name: "pons-cpu-miner" },
  )));
}

export function createGpuWorkerBackend(onCapability: (capability: GpuCapability) => void): MiningBackend {
  return new ModuleWorkerMiningBackend("GPU", () => new Worker(
    new URL("./workers/gpu-miner.worker.ts", import.meta.url),
    { type: "module", name: "pons-gpu-miner" },
  ), onCapability);
}

function abortError(): Error { return new DOMException("Mining batch aborted", "AbortError"); }
